import React from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import "./FeaturePages.css";

import { SITE_URL } from "../../utils/siteSeo.mjs";

const featureScreenshotGallery = [
  {
    src: "/features/feature-armory-status.webp",
    label: "Armory status dashboard",
  },
  {
    src: "/features/feature-firearm-list.webp",
    label: "Firearm inventory list",
  },
  {
    src: "/features/feature-inventory-dashboard.webp",
    label: "Inventory cost dashboard",
  },
  {
    src: "/features/feature-gunsmith-profile.webp",
    label: "Gunsmith and accessory profile",
  },
  {
    src: "/features/feature-range-map.webp",
    label: "Nearby range map",
  },
  {
    src: "/features/feature-range-visits.webp",
    label: "Range visit records",
  },
  {
    src: "/features/feature-visit-activity.webp",
    label: "Visit activity details",
  },
  {
    src: "/features/feature-training-overview.webp",
    label: "Training overview",
  },
];

export const RANGE_POCKET_FEATURES = {
  "/features": {
    title: "Range Pocket Features | Firearm Inventory, Range Logs & Training",
    description:
      "Explore Range Pocket features for firearm inventory, range visit logging, ammo tracking, maintenance, training plans, accessories, saved ranges, photos, and notes.",
    h1: "Range Pocket features for the full range workflow.",
    kicker: "Features",
    intro:
      "Track firearms, range visits, training, maintenance, accessories, photos, and personal notes in one connected workflow.",
    image: "/features/feature-armory-status.webp",
    heroImages: [
      "/features/feature-armory-status.webp",
      "/features/feature-firearm-list.webp",
      "/features/feature-range-visits.webp",
    ],
    galleryImages: featureScreenshotGallery,
    primaryCta: "Explore Range Pocket",
    sections: [
      "Firearm inventory with photos, purchase notes, accessories, and usage history.",
      "Range visit logs that connect firearms, round counts, target photos, drills, and notes.",
      "Maintenance, battery reminders, saved ranges, checklists, and training plans in one app.",
    ],
    faqs: [
      {
        q: "What features does Range Pocket include?",
        a: "Range Pocket includes firearm inventory, ammo and accessory tracking, range visit logs, target photos, training plans, maintenance records, saved ranges, photos, and notes.",
      },
      {
        q: "Is Range Pocket only for firearm inventory?",
        a: "No. Inventory is one part of the app. Range Pocket also connects range logs, training plans, maintenance, accessories, saved ranges, and usage history.",
      },
    ],
  },
  "/features/firearm-inventory": {
    title: "Firearm Inventory Tracking Feature | Range Pocket",
    description:
      "Track firearm photos, purchase details, accessories, configurations, maintenance notes, round counts, and range usage history with Range Pocket.",
    h1: "Firearm inventory that connects to real use.",
    kicker: "Firearm Inventory",
    intro:
      "Keep firearm photos, purchase details, accessories, round history, range visits, and maintenance context together.",
    image: "/features/feature-firearm-list.webp",
    primaryCta: "View inventory app page",
    primaryHref: "/firearm-inventory-app",
    sections: [
      "Store make, model, caliber, source, purchase date, photos, and personal notes.",
      "Connect each firearm to range visits, round counts, target photos, and training history.",
      "Keep accessory changes, maintenance context, and receipts near the record they belong to.",
    ],
    faqs: [
      {
        q: "Can I add photos to firearm records?",
        a: "Yes. Range Pocket is designed to keep firearm photos and record details together so the inventory is easier to review.",
      },
      {
        q: "Can inventory connect to range history?",
        a: "Yes. Firearm records can connect to range visits and round counts, giving each firearm a more useful usage history.",
      },
    ],
  },
  "/features/range-visit-logging": {
    title: "Range Visit Logging Feature for Shooters | Range Pocket",
    description:
      "Use Range Pocket as a range log app for firearms used, round counts, target photos, notes, saved ranges, drills, conditions, and training context.",
    h1: "Range visit logging built for real sessions.",
    kicker: "Range Visit Logging",
    intro:
      "Log the firearms, round counts, notes, drills, ranges, and target photos that make each session useful later.",
    image: "/features/feature-range-visits.webp",
    primaryCta: "View range log app page",
    primaryHref: "/range-log-app",
    sections: [
      "Record range, date, firearms used, round counts, notes, target photos, and conditions.",
      "Use saved ranges and checklists to prepare faster before a session.",
      "Review previous visits before the next trip so training decisions are based on history.",
    ],
    faqs: [
      {
        q: "Can I attach target photos to a range visit?",
        a: "Yes. Range Pocket supports target photos and notes so each visit has visual context.",
      },
      {
        q: "Can range visits update firearm history?",
        a: "Yes. Range visits can connect back to the firearms used, helping build usage and round-count history.",
      },
    ],
  },
  "/features/ammo-tracking": {
    title: "Ammo Tracking Feature for Firearm Owners | Range Pocket",
    description:
      "Keep ammunition records, usage context, firearm compatibility, range visit history, and inventory notes connected in Range Pocket.",
    h1: "Ammo tracking that belongs with your range records.",
    kicker: "Ammo Tracking",
    intro:
      "Keep ammunition context close to the firearms, range visits, and notes where it actually matters.",
    image: "/features/feature-inventory-dashboard.webp",
    primaryCta: "Explore Range Pocket",
    sections: [
      "Keep ammunition records alongside firearms, accessories, range visits, and training notes.",
      "Use range visit history to understand what was used and when.",
      "Reduce scattered notes by keeping ammo context in the same workflow as inventory and logs.",
    ],
    faqs: [
      {
        q: "Does Range Pocket help organize ammunition records?",
        a: "Yes. Range Pocket is designed to keep ammo context near firearm inventory, range visits, and training history.",
      },
      {
        q: "Why connect ammo tracking to range visits?",
        a: "Range visit context makes ammo records more useful because it shows how equipment was actually used over time.",
      },
    ],
  },
  "/features/firearm-maintenance": {
    title: "Firearm Maintenance Tracking Feature | Range Pocket",
    description:
      "Track firearm maintenance notes, cleaning history, round counts, accessory changes, battery reminders, photos, and range use in Range Pocket.",
    h1: "Maintenance records connected to round counts and range use.",
    kicker: "Firearm Maintenance",
    intro:
      "Connect cleaning notes, round counts, accessory changes, and reminders to the firearms they belong to.",
    image: "/content/blog/firearm-maintenance-log-what-to-track/maintenance_plan_screen.png",
    primaryCta: "View maintenance tracker",
    primaryHref: "/firearm-maintenance-tracker",
    sections: [
      "Track cleaning notes, maintenance history, photos, and configuration changes.",
      "Use round counts and range history as context for follow-up maintenance.",
      "Set reminders for accessories and battery-powered equipment before the next session.",
    ],
    faqs: [
      {
        q: "Can maintenance records be tied to a firearm?",
        a: "Yes. Range Pocket keeps maintenance context organized around the firearm it belongs to.",
      },
      {
        q: "Can I track accessory battery changes?",
        a: "Yes. Range Pocket includes accessory tracking and battery reminder workflows.",
      },
    ],
  },
  "/features/training-plans": {
    title: "Shooting Training Plan Feature | Range Pocket",
    description:
      "Create shooting training plans, custom drills, range visit checklists, and progress history that connects back to your Range Pocket logs.",
    h1: "Training plans that stay connected to range visits.",
    kicker: "Training Plans",
    intro:
      "Organize drills, plans, checklists, and session history so each range day builds on the last.",
    image: "/features/feature-training-overview.webp",
    primaryCta: "Explore Range Pocket",
    sections: [
      "Create custom drills and organize them into reusable training plans.",
      "Attach plans to range visits so practice goals are visible during the session.",
      "Review completed work later alongside notes, firearms, and target photos.",
    ],
    faqs: [
      {
        q: "Can I create my own drills?",
        a: "Yes. Range Pocket supports custom drills and training plans that can be reused across range visits.",
      },
      {
        q: "Can training plans connect to range sessions?",
        a: "Yes. Training plans can be used as part of range visit workflows so practice stays structured.",
      },
    ],
  },
  "/features/accessory-tracking": {
    title: "Firearm Accessory Tracking Feature | Range Pocket",
    description:
      "Track firearm accessories, optics, parts, battery reminders, custom builds, linked equipment, and configuration records with Range Pocket.",
    h1: "Accessory tracking for optics, parts, batteries, and builds.",
    kicker: "Accessory Tracking",
    intro:
      "Track optics, gear, parts, battery reminders, and custom build context without spreading details across notes.",
    image: "/features/feature-gunsmith-profile.webp",
    primaryCta: "Explore Range Pocket",
    sections: [
      "Track accessories and connect them to firearms or custom profiles.",
      "Use battery reminders for optics, electronic hearing protection, and other powered gear.",
      "Keep configuration changes and build notes close to inventory history.",
    ],
    faqs: [
      {
        q: "Can accessories be linked to firearms?",
        a: "Yes. Range Pocket is designed to keep accessory and configuration context connected to relevant firearm records.",
      },
      {
        q: "Does Range Pocket support battery reminders?",
        a: "Yes. Battery reminders help users prepare powered accessories before a range session.",
      },
    ],
  },
};

const featureLinks = Object.entries(RANGE_POCKET_FEATURES)
  .filter(([path]) => path !== "/features")
  .map(([path, feature]) => ({ path, ...feature }));

function faqJsonLd(page) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: page.faqs.map((item) => ({
      "@type": "Question",
      name: item.q,
      acceptedAnswer: { "@type": "Answer", text: item.a },
    })),
  };
}

function itemListJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "Range Pocket features",
    itemListElement: featureLinks.map((feature, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `${SITE_URL}${feature.path}`,
      name: feature.kicker,
    })),
  };
}

function FeaturePage({ page, path }) {
  const canonical = `${SITE_URL}${path}`;
  const isHub = path === "/features";
  const imageUrl = `${SITE_URL}${page.image}`;

  return (
    <main className="features-page">
      <Helmet>
        <title>{page.title}</title>
        <meta name="description" content={page.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={page.title} />
        <meta property="og:description" content={page.description} />
        <meta property="og:url" content={canonical} />
        <meta property="og:image" content={imageUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={page.title} />
        <meta name="twitter:description" content={page.description} />
        <meta name="twitter:image" content={imageUrl} />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(page))}</script>
        {isHub && <script type="application/ld+json">{JSON.stringify(itemListJsonLd())}</script>}
      </Helmet>

      <section className="features-hero">
        <div className="features-copy">
          <div className="features-kicker">{page.kicker}</div>
          <h1>{page.h1}</h1>
          <p>{page.intro}</p>
          <div className="features-actions">
            <Link className="features-button features-button-primary" to={page.primaryHref || "/range-pocket"}>
              {page.primaryCta}
            </Link>
            {!isHub && (
              <Link className="features-button features-button-secondary" to="/features">
                Range Pocket features
              </Link>
            )}
          </div>
        </div>
        <div
          className={`features-media${page.heroImages ? " features-media-collage" : ""}`}
          aria-hidden="true"
        >
          {page.heroImages ? (
            page.heroImages.map((image) => <img src={image} alt="" key={image} />)
          ) : (
            <img src={page.image} alt="" />
          )}
        </div>
      </section>

      <section className="features-points" aria-label={`${page.kicker} highlights`}>
        {page.sections.map((item) => (
          <article key={item}>
            <p>{item}</p>
          </article>
        ))}
      </section>

      {isHub && (
        <>
          <section className="features-directory" aria-label="Range Pocket feature directory">
            {featureLinks.map((feature) => (
              <Link to={feature.path} key={feature.path}>
                <img src={feature.image} alt="" loading="lazy" />
                <span>{feature.kicker}</span>
                <strong>{feature.h1}</strong>
              </Link>
            ))}
          </section>

          <section className="features-screenshot-gallery" aria-labelledby="range-pocket-screenshots">
            <div className="features-kicker">Real App Screens</div>
            <h2 id="range-pocket-screenshots">Range Pocket screens from real workflows</h2>
            <div>
              {page.galleryImages.map((screenshot) => (
                <figure key={screenshot.src}>
                  <img src={screenshot.src} alt={screenshot.label} loading="lazy" />
                  <figcaption>{screenshot.label}</figcaption>
                </figure>
              ))}
            </div>
          </section>
        </>
      )}

      <section className="features-faq" aria-labelledby={`${path.slice(1).replaceAll("/", "-")}-faq`}>
        <div className="features-kicker">Common Questions</div>
        <h2 id={`${path.slice(1).replaceAll("/", "-")}-faq`}>{page.kicker} FAQ</h2>
        <div className="features-faq-list">
          {page.faqs.map((item) => (
            <article key={item.q}>
              <h3>{item.q}</h3>
              <p>{item.a}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="features-related" aria-label="Related Range Pocket pages">
        <Link to="/range-pocket">Range Pocket</Link>
        <Link to="/firearm-inventory-app">Firearm inventory app</Link>
        <Link to="/range-log-app">Range log app</Link>
        <Link to="/firearm-maintenance-tracker">Maintenance tracker</Link>
        <Link to="/blog/range-pocket-records">How records work</Link>
      </section>
    </main>
  );
}

export const FeatureRoutes = Object.entries(RANGE_POCKET_FEATURES).map(([path, page]) => ({
  path,
  element: <FeaturePage page={page} path={path} />,
}));

export default FeaturePage;
